import { c as _c } from "react/compiler-runtime";
import { getEntitiesByFpIdOnboardingsByOnboardingIdRiskSignalsOptions, getEntitiesByFpIdOnboardingsByOnboardingIdTraceOptions } from '@onefootprint/fetch-dashboard';
import type { EntityOnboarding } from '@onefootprint/request-types/dashboard';
import { useQuery } from '@tanstack/react-query';
import { cx } from 'class-variance-authority';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BUSINESS_INSIGHT_SUBSECTIONS, type BusinessInsightSubsection, isAwaitingResults, ObeRules, OnboardingDocuments, OnboardingVaultData, type SubsectionType as Subsection, Subsection as SubsectionBody } from '@/components/entity-onboarding-details';
import type { WithEntityProps } from '@/entity/components/with-entity';
import OnboardingMoreInfo from '../../../onboardings-shared/components/onboarding-more-info';
import useOnboardingVault from '../../../onboardings-shared/hooks/use-onboarding-vault';
import useShowOnboarding from '../../../onboardings-shared/hooks/use-show-onboarding';
import { Decrypt, DocumentsDecrypt, EncryptedBusinessInsights, groupRiskSignals, OnboardingFieldValidations, OnboardingRiskSignals, OnboardingRules } from '../onboarding-details';
import BusinessInsight from './business-insight';
import useSections from './use-sections';
type OnboardingPanelProps = WithEntityProps & {
  onboarding: EntityOnboarding;
};
const isBusinessInsight = (key: Subsection): key is BusinessInsightSubsection => (BUSINESS_INSIGHT_SUBSECTIONS as readonly Subsection[]).includes(key);
const OnboardingPanel = t0 => {
  const $ = _c(53);
  const {
    entity,
    onboarding
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "classicOnboardings"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const sections = useSections(onboarding);
  const {
    onboardingSection,
    setOnboardingSection
  } = useShowOnboarding();
  let t2;
  if ($[1] !== onboardingSection || $[2] !== sections) {
    t2 = sections.find(section => section.key === onboardingSection) ?? sections[0];
    $[1] = onboardingSection;
    $[2] = sections;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const selected = t2;
  const {
    vault,
    canDecrypt,
    isDecrypted,
    showDecrypt
  } = useOnboardingVault(entity, onboarding);
  let t3;
  if ($[4] !== entity.id || $[5] !== onboarding.id) {
    t3 = getEntitiesByFpIdOnboardingsByOnboardingIdRiskSignalsOptions({
      path: {
        fpId: entity.id,
        onboardingId: onboarding.id
      }
    });
    $[4] = entity.id;
    $[5] = onboarding.id;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    data: riskSignals,
    isPending: isRiskSignalsPending
  } = useQuery(t3);
  let t4;
  if ($[7] !== entity.id || $[8] !== onboarding.id) {
    t4 = getEntitiesByFpIdOnboardingsByOnboardingIdTraceOptions({
      path: {
        fpId: entity.id,
        onboardingId: onboarding.id
      }
    });
    $[7] = entity.id;
    $[8] = onboarding.id;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== onboarding.hasGraphTrace || $[11] !== t4) {
    t5 = {
      ...t4,
      enabled: onboarding.hasGraphTrace
    };
    $[10] = onboarding.hasGraphTrace;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const {
    data: trace
  } = useQuery(t5);
  let t6;
  if ($[13] !== canDecrypt || $[14] !== entity || $[15] !== onboarding || $[16] !== vault.data || $[17] !== vault.update) {
    t6 = <Decrypt canDecrypt={canDecrypt} onDecryptSuccess={vault.update} onboarding={onboarding} vaultData={vault.data} entity={entity} />;
    $[13] = canDecrypt;
    $[14] = entity;
    $[15] = onboarding;
    $[16] = vault.data;
    $[17] = vault.update;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const vaultDecrypt = t6;
  let t7;
  if ($[19] !== canDecrypt || $[20] !== entity || $[21] !== isDecrypted || $[22] !== isRiskSignalsPending || $[23] !== onboarding || $[24] !== riskSignals || $[25] !== showDecrypt || $[26] !== trace || $[27] !== vault || $[28] !== vaultDecrypt) {
    t7 = key => {
      if (isBusinessInsight(key)) {
        return {
          content: isDecrypted ? <BusinessInsight onboarding={onboarding} subsection={key} /> : <EncryptedBusinessInsights canDecrypt={canDecrypt} entity={entity} onboarding={onboarding} onDecryptSuccess={vault.update} vaultData={vault.data} />
        };
      }
      switch (key) {
        case "rules":
          {
            if (!onboarding.hasGraphTrace) {
              return {
                content: <OnboardingRules ruleSetResults={onboarding.ruleSetResults} />
              };
            }
            return {
              content: trace ? <ObeRules trace={trace} isAwaitingRules={isAwaitingResults(onboarding)} /> : null
            };
          }
        case "risk-signals":
          {
            return {
              content: <OnboardingRiskSignals isLoading={isRiskSignalsPending} riskSignals={groupRiskSignals(riskSignals ?? [])} />
            };
          }
        case "user-data":
        case "business-data":
          {
            return {
              action: showDecrypt ? vaultDecrypt : undefined,
              content: <OnboardingVaultData entity={entity} vault={vault} />
            };
          }
        case "field-validations":
          {
            return {
              content: <SubsectionBody><OnboardingFieldValidations entityId={entity.id} onboardingId={onboarding.id} /></SubsectionBody>
            };
          }
        case "documents":
          {
            return {
              action: <DocumentsDecrypt entity={entity} onboarding={onboarding} />,
              content: <OnboardingDocuments entity={entity} onboarding={onboarding} />
            };
          }
        default:
          {
            return {
              content: <OnboardingMoreInfo onboarding={onboarding} />
            };
          }
      }
    };
    $[19] = canDecrypt;
    $[20] = entity;
    $[21] = isDecrypted;
    $[22] = isRiskSignalsPending;
    $[23] = onboarding;
    $[24] = riskSignals;
    $[25] = showDecrypt;
    $[26] = trace;
    $[27] = vault;
    $[28] = vaultDecrypt;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  const getContent = t7;
  let t8;
  if ($[30] !== getContent || $[31] !== selected.key) {
    t8 = getContent(selected.key);
    $[30] = getContent;
    $[31] = selected.key;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  const {
    action,
    content
  } = t8;
  let t9;
  if ($[33] !== t) {
    t9 = t(_temp);
    $[33] = t;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  let t10;
  if ($[35] !== sections || $[36] !== selected.key || $[37] !== setOnboardingSection) {
    let t11;
    if ($[39] !== selected.key || $[40] !== setOnboardingSection) {
      t11 = t12 => {
        const {
          key: key_0,
          title
        } = t12;
        const isSelected = key_0 === selected.key;
        return <button key={key_0} type="button" aria-current={isSelected ? "true" : undefined} className={cx("py-2 mx-3 text-label-2 text-nowrap border-b border-solid cursor-pointer transition-colors duration-150 hover:text-primary", {
          "text-primary border-primary": isSelected,
          "text-tertiary border-transparent": !isSelected
        })} onClick={() => setOnboardingSection(key_0)}>{title}</button>;
      };
      $[39] = selected.key;
      $[40] = setOnboardingSection;
      $[41] = t11;
    } else {
      t11 = $[41];
    }
    t10 = sections.map(t11);
    $[35] = sections;
    $[36] = selected.key;
    $[37] = setOnboardingSection;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  let t11;
  if ($[42] !== t10 || $[43] !== t9) {
    t11 = <nav aria-label={t9} className="flex items-center gap-0.5 px-3 pt-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">{t10}</nav>;
    $[42] = t10;
    $[43] = t9;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  let t12;
  if ($[45] !== action || $[46] !== t11) {
    t12 = <div className="flex items-center justify-between gap-3 pr-4 border-b border-solid border-tertiary">{t11}{action}</div>;
    $[45] = action;
    $[46] = t11;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  let t13;
  if ($[48] !== content) {
    t13 = <div className="@container flex-1 overflow-y-auto">{content}</div>;
    $[48] = content;
    $[49] = t13;
  } else {
    t13 = $[49];
  }
  let t14;
  if ($[50] !== t12 || $[51] !== t13) {
    t14 = <div className="flex flex-col w-full h-[520px] min-w-0 overflow-hidden border border-solid rounded bg-primary border-tertiary md:h-full md:ml-0.5">{t12}{t13}</div>;
    $[50] = t12;
    $[51] = t13;
    $[52] = t14;
  } else {
    t14 = $[52];
  }
  return t14;
};
export default OnboardingPanel;
function _temp($) {
  return $.sections;
}